import React, { useEffect, useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { NewsList } from '../components/news';
import { useNews, useCategories } from '../hooks';
import SEOMetaTags from '../components/SEOMetaTags';

const CategoryPage: React.FC = () => {
  const { categoryId: slugOrId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();

  const { categories, loading: loadingCategories, setSelectedCategory } = useCategories();
  
  // Slug veya ID ile kategoriyi bul
  const currentCategory = useMemo(() => {
    if (!categories.length || !slugOrId) return null;
    return categories.find(cat => cat.slug === slugOrId || cat.id === slugOrId) || null;
  }, [categories, slugOrId]);

  // CategoryId'yi al - null ise undefined olarak ver ki ana sayfa haberleri gelmesin
  const categoryId = currentCategory ? currentCategory.id : undefined;

  const { 
    news, 
    pagination, 
    loading, 
    error, 
    changePage
  } = useNews({ categoryId });

  // Kategori kontrolü ve yönlendirme
  useEffect(() => {
    if (!slugOrId || loadingCategories || !categories.length) {
      return;
    }

    if (currentCategory) {
      setSelectedCategory(currentCategory.id);
    } else {
      // Categories yüklendi ama kategori bulunamadı
      navigate('/404');
    }
  }, [slugOrId, loadingCategories, categories.length, navigate]);

  // Convert from pagination object to the one NewsList component expects
  const paginationProps = {
    count: pagination.totalPages,
    page: pagination.number + 1,
    onChange: (event: React.ChangeEvent<unknown>, page: number) => {
      changePage(page - 1);
    }
  };

  // Kategori bulunamadıysa hiçbir şey render etme
  if (!loadingCategories && !currentCategory) {
    return null;
  }

  return (
    <>
      {/* SEO Meta Tags */}
      {currentCategory && (
        <SEOMetaTags
          title={`${currentCategory.name} Haberleri`}
          description={`${currentCategory.name} kategorisindeki en güncel haberler, son dakika gelişmeleri - Akademik Kadro`}
          keywords={`${currentCategory.name}, haberler, güncel haberler, akademik kadro`}
          url={window.location.href}
          type="website"
        />
      )}
      
      {/* Main content */}
      <Box>


        {/* Category title and description */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            {currentCategory ? currentCategory.name : 'Kategori Haberleri'}
          </Typography>
          {currentCategory && currentCategory.description && (
            <Typography variant="body1" color="text.secondary">
              {currentCategory.description}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* News Listing */}
        <NewsList
          news={news}
          loading={loading || loadingCategories}
          error={error}
          pagination={paginationProps}
          emptyMessage={`${currentCategory ? currentCategory.name : 'Bu kategori'} için henüz haber bulunmamaktadır.`}
        />
      </Box>
    </>
  );
};

export default CategoryPage; 